/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Package, ChevronRight, AlertTriangle, MapPin } from 'lucide-react';
import ClienteLayout from '../components/ClienteLayout';
import { prodottiAPI } from '../api/prodotti';

const ACCENT = '#3B82F6';
const COLORI = { verde: '#10B981', arancione: '#F59E0B', rosso: '#EF4444', scaduta: '#DC2626' };
const COLORE_LABEL = { verde: 'In regola', arancione: 'In avvicinamento', rosso: 'Imminente', scaduta: 'Scaduta' };

export default function Prodotti() {
  const navigate = useNavigate();
  const { data: prodotti = [], isLoading } = useQuery({ queryKey: ['prodotti-cliente'], queryFn: () => prodottiAPI.miei() });

  return (
    <ClienteLayout title="I miei prodotti">
      {isLoading ? (
        <div style={styles.loading}>Caricamento...</div>
      ) : prodotti.length === 0 ? (
        <div style={styles.empty}>
          <Package size={40} color="#9CA3AF" />
          <p>Non risultano prodotti registrati.</p>
          <p style={{ fontSize: '0.85rem', color: '#9CA3AF' }}>I prodotti installati vengono aggiunti dal nostro staff.</p>
        </div>
      ) : (
        <div style={styles.list}>
          {prodotti.map((p) => {
            const scadute = p.manutenzioni.filter(m => m.colore === 'scaduta' || m.colore === 'rosso').length;
            return (
              <div key={p.id} style={styles.card} onClick={() => navigate(`/prodotti/${p.id}`)}>
                <div style={styles.cardTop}>
                  <div style={{ ...styles.iconBox, backgroundColor: ACCENT + '15' }}>
                    <Package size={24} color={ACCENT} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.tipoNome}>{p.tipo_nome}</div>
                    <div style={styles.marcaModello}>{[p.marca, p.modello].filter(Boolean).join(' ')}</div>
                    {p.ubicazione && <div style={styles.ubicazione}><MapPin size={12} /> {p.ubicazione}</div>}
                  </div>
                  <ChevronRight size={20} color="#9CA3AF" />
                </div>
                {scadute > 0 && (
                  <div style={styles.alertBadge}>
                    <AlertTriangle size={14} /> {scadute} manutenzion{scadute > 1 ? 'i' : 'e'} da pianificare
                  </div>
                )}
                <div style={styles.maintList}>
                  {p.manutenzioni.map((m) => (
                    <div key={m.manutenzione_tipo_id} style={styles.maintRow}>
                      <div style={styles.maintInfo}>
                        <span style={styles.maintNome}>{m.nome}{m.opzionale ? ' (opz.)' : ''}</span>
                        <span style={{ ...styles.maintStato, color: COLORI[m.colore] }}>{COLORE_LABEL[m.colore]}</span>
                      </div>
                      <div style={styles.barTrack}>
                        <div style={{ ...styles.barFill, width: `${m.percentuale_rimanente}%`, backgroundColor: COLORI[m.colore] }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </ClienteLayout>
  );
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  empty: { padding: '3rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', backgroundColor: 'white', borderRadius: '12px' },
  list: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', cursor: 'pointer' },
  cardTop: { display: 'flex', alignItems: 'center', gap: '0.85rem' },
  iconBox: { width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  tipoNome: { fontWeight: 700, fontSize: '1.05rem' },
  marcaModello: { fontSize: '0.88rem', color: '#4B5563' },
  ubicazione: { display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', color: '#9CA3AF', marginTop: '0.15rem' },
  alertBadge: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.35rem 0.7rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, marginTop: '0.85rem' },
  maintList: { display: 'flex', flexDirection: 'column', gap: '0.7rem', marginTop: '1rem' },
  maintRow: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  maintInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  maintNome: { fontSize: '0.85rem', color: '#374151', fontWeight: 500 },
  maintStato: { fontSize: '0.78rem', fontWeight: 700 },
  barTrack: { height: '8px', backgroundColor: '#F3F4F6', borderRadius: '4px', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: '4px', transition: 'width 0.4s' },
};
